import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import LotusMark from '@/components/generals/lotus-mark'

interface ArcArtProps {
    image?: string | null
    alt: string
    number: number
    className?: string
    sizes?: string
}

const ArcArt: FC<ArcArtProps> = ({
    image,
    alt,
    number,
    className,
    sizes = '100vw'
}): ReactNode => {
    const src = typeof image === 'string' ? image.trim() : ''

    if (src) {
        return (
            <div className={cn('relative isolate aspect-[16/9] w-full overflow-hidden rounded-3xl bg-night', className)}>
                <Image
                    src={src}
                    alt={alt}
                    fill
                    sizes={sizes}
                    className='object-cover'
                />

                <div aria-hidden='true' className='pointer-events-none absolute inset-0 rounded-[inherit] border border-gold/25' />
            </div>
        )
    }

    const mirrored = number % 2 === 0

    return (
        <div
            aria-hidden='true'
            className={cn('night-sky @container relative isolate aspect-[16/9] w-full overflow-hidden rounded-3xl', className)}
        >
            <div
                className={cn(
                    'absolute inset-0',
                    mirrored
                        ? 'bg-[radial-gradient(ellipse_at_88%_110%,color-mix(in_oklch,var(--vermilion)_72%,transparent),transparent_62%)]'
                        : 'bg-[radial-gradient(ellipse_at_12%_110%,color-mix(in_oklch,var(--vermilion)_72%,transparent),transparent_62%)]'
                )}
            />

            <div
                className={cn(
                    'absolute inset-0',
                    mirrored
                        ? 'bg-[radial-gradient(circle_at_26%_50%,color-mix(in_oklch,var(--gold)_32%,transparent),transparent_40%)]'
                        : 'bg-[radial-gradient(circle_at_74%_50%,color-mix(in_oklch,var(--gold)_32%,transparent),transparent_40%)]'
                )}
            />

            <div className={cn('absolute top-1/2', mirrored ? 'left-[26%]' : 'right-[26%]')}>
                <div className='absolute left-0 top-0 size-[66cqw] -translate-x-1/2 -translate-y-1/2 rounded-full border border-gold/10' />
                <div className='absolute left-0 top-0 size-[50cqw] -translate-x-1/2 -translate-y-1/2 rounded-full border border-gold/15' />
                <div className='animate-slow-spin absolute left-0 top-0 size-[38cqw] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-gold/30' />
                <LotusMark className='absolute left-0 top-0 size-[32cqw] -translate-x-1/2 -translate-y-1/2 opacity-25' />
            </div>

            <div className={cn('absolute inset-y-0 flex flex-col justify-center', mirrored ? 'right-[8%] items-end' : 'left-[8%] items-start')}>
                <span className='font-heading text-[length:clamp(3.5rem,20cqw,14rem)] font-semibold leading-[0.85] text-gold-soft lining-nums [text-shadow:0_10px_40px_rgb(0_0_0/0.45)]'>
                    {number}
                </span>

                <span className={cn('mt-[2.5cqw] h-px w-[16cqw] from-gold to-transparent', mirrored ? 'bg-linear-to-l' : 'bg-linear-to-r')} />
            </div>

            <div className='absolute inset-3 rounded-2xl border border-gold/15 sm:inset-4' />
            <div className='absolute inset-x-[12%] bottom-0 h-px bg-linear-to-r from-transparent via-gold/60 to-transparent' />
        </div>
    )
}

export default ArcArt
